/**
 * PermissionsInfo. Information in this object can be used to safely determine
 * information about the current user and can be accessed even during
 * server-side rendering. It should be available on initial load.
 *
 * Contains info that comes from the KAAC cookie (as parsed by Fastly).
 *
 * NOTE(frontend-infra): Please do not add properties to this, or any other,
 * info objects without first consulting with us!
 */
import {kaHasAnyPermissions, kaHasProfile, kaIsPhantom} from "./fastly-data.ts";

/**
 * NOTE(frontend-infra): Tests that are using PermissionsInfo should mock the
 * accessors with:
 *      jest.spyOn(PermissionsInfo, "property", "get").mockReturnValue("value")
 *
 * Do not rely on the internal implementation of PermissionsInfo.
 */

const PermissionsInfo = {
    /**
     * True for privileged users such as developers and content creators.
     * False for regular users such as students and coaches.
     */
    get hasAnyPermissions(): boolean {
        // The user has any permissions if "has-any-permissions" is set to
        // true. This can never be true for a logged-out user.
        return kaHasAnyPermissions();
    },
    /**
     * True is the user is logged in. False for phantom users.
     */
    get isLoggedIn(): boolean {
        return kaHasProfile() && !kaIsPhantom();
    },
    /**
     * This is true only for users who have taken a learning action.
     * These users are not considered logged-in but do have a user
     * profile.
     */
    get isPhantom(): boolean {
        return kaHasProfile() && kaIsPhantom();
    },
    /**
     * True for logged-in users or phantom users.
     */
    get hasProfile(): boolean {
        return kaHasProfile();
    },
};

export default PermissionsInfo;
